import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import Index from "./pages/Index";

// Lazy-load toast UI to reduce unused JS on initial load
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("@/components/CookieBanner"));

// Lazy load non-critical routes
const NotFound = lazy(() => import("./pages/NotFound"));
const ThemaDetail = lazy(() => import("./pages/ThemaDetail"));
const AdviesOverzicht = lazy(() => import("./pages/AdviesOverzicht"));
const Ondertekenaars = lazy(() => import("./pages/Ondertekenaars"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NewsletterPreview = lazy(() => import("./pages/NewsletterPreview"));
// SubPages has no default export, individual lazy imports above handle it

const LazyFeitenPage = lazy(() => import("./pages/SubPages").then(m => ({ default: m.FeitenPage })));
const LazyCijfersPage = lazy(() => import("./pages/SubPages").then(m => ({ default: m.CijfersPage })));
const LazyStemmenPage = lazy(() => import("./pages/SubPages").then(m => ({ default: m.StemmenPage })));
const LazyVerkiezingenPage = lazy(() => import("./pages/SubPages").then(m => ({ default: m.VerkiezingenPage })));
const LazyMediaPage = lazy(() => import("./pages/SubPages").then(m => ({ default: m.MediaPage })));

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Suspense fallback={null}><Toaster /></Suspense>
        <Suspense fallback={null}><Sonner /></Suspense>
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/feiten" element={<LazyFeitenPage />} />
              <Route path="/cijfers" element={<LazyCijfersPage />} />
              <Route path="/veiligheid" element={<LazyCijfersPage />} />
              <Route path="/stemmen" element={<LazyStemmenPage />} />
              <Route path="/verkiezingen" element={<LazyVerkiezingenPage />} />
              <Route path="/media" element={<LazyMediaPage />} />
              <Route path="/thema/:slug" element={<ThemaDetail />} />
              <Route path="/advies" element={<AdviesOverzicht />} />
              <Route path="/ondertekenaars" element={<Ondertekenaars />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/newsletter-preview" element={<NewsletterPreview />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}><CookieBanner /></Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
